import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdAdd } from "react-icons/md";
import Helpers from "../../components/Controls/helpers";
import ConfirmDlg from "../Controls/ConfirmDlg";
import AutoComplete from "../Controls/AutoComplete";
import SkillEditModal from "../Modals/SkillEditModal";
import SkillResolveModal from "../Modals/SkillResolveModal";
import API from "@src/API";
import { usePersonSkillsQuery } from "../../query/usePersonSkills";

function normalizeSkill(raw) {
  if (!raw || typeof raw === "string") return null;
  const source = (raw.Source ?? raw.source ?? "catalog") || "catalog";
  return {
    ID: raw.ID ?? raw.id ?? 0,
    Name: raw.Name ?? raw.name ?? "",
    ShortName: raw.ShortName ?? raw.shortName ?? raw.Name ?? raw.name ?? "",
    Description: raw.Description ?? raw.description ?? "",
    CategoryName: raw.CategoryName ?? raw.categoryName ?? "",
    Source: source,
    isOnet: String(source).toLowerCase() === "onet",
  };
}

function candidateToDraft(candidate, fallbackName = "") {
  const name = (candidate?.Name || fallbackName || "").trim();
  return {
    ID: 0,
    ParentID: 0,
    ParentSkillName: "",
    Name: name,
    ShortName: (candidate?.ShortName || name).trim(),
    CategoryID: candidate?.CategoryID || 0,
    CategoryName: candidate?.CategoryName || "",
    Description: candidate?.Description || "",
    ReleaseDate: candidate?.ReleaseDate || candidate?.releaseDate || null,
    Version: "",
    WikiUrl: candidate?.WikiUrl || "",
    Status: "pending",
  };
}

export default function SkillSearch(props) {
  const navigate = useNavigate();
  const [dlgOpen, setDlgOpen] = useState(false);
  const { data: skillsData } = usePersonSkillsQuery();
  const knownSkills = skillsData?.skills || [];
  const [skillSearchResults, updateSearchResults] = useState([]);
  const [searchData, setSearchData] = useState({ term: "", results: [] });
  const [searchBusy, setSearchBusy] = useState(false);
  const [pendingSkill, setPendingSkill] = useState(null);
  const [createDraft, setCreateDraft] = useState(null);
  const [resolveState, setResolveState] = useState(null);
  const displayField = props.fieldDisplay || "";
  const [inputValue, setInputValue] = useState(() =>
    props.object && displayField ? props.object[displayField] || "" : ""
  );
  const searchSeq = useRef(0);
  const debounceRef = useRef(null);

  // When used as a bound field (e.g. Parent Skill), keep the input in sync with the form object.
  const boundDisplay =
    props.object && displayField ? props.object[displayField] || "" : undefined;
  useEffect(() => {
    if (boundDisplay === undefined) return;
    setInputValue(boundDisplay);
  }, [boundDisplay]);

  const allowCreate = props.allowCreate !== false && !!(props.onSelect || props.onChange);
  // Parent / lookup-only pickers should not offer O*NET create stubs.
  const includeOnet = allowCreate;

  const trimmedInput = (inputValue || "").trim();
  const hasExactMatch = useMemo(() => {
    if (!trimmedInput) return false;
    const needle = trimmedInput.toLowerCase();
    const pool = [
      ...(searchData.results || []),
      ...skillSearchResults.filter((s) => (s.ID ?? s.id) > 0),
    ];
    return pool.some(
      (s) =>
        (s.Name || s.name || "").toLowerCase() === needle ||
        (s.ShortName || s.shortName || "").toLowerCase() === needle
    );
  }, [trimmedInput, searchData.results, skillSearchResults]);

  const showCreateButton = allowCreate && trimmedInput.length >= 2 && !hasExactMatch;

  const dropdownOptions = useMemo(() => {
    const opts = skillSearchResults
      .filter((s) => includeOnet || !s.isOnet)
      .map((s) => {
        const isOnet = !!s.isOnet;
        return {
          id: isOnet ? `__onet__:${(s.Name || "").toLowerCase()}` : s.ID,
          label: s.Name,
          secondary: isOnet
            ? "O*NET — look up & create"
            : s.ShortName && s.ShortName !== s.Name
              ? s.ShortName
              : "",
          value: s,
          isOnet,
          isCreate: isOnet,
        };
      });

    // Catalog has no PostgreSQL (etc.) — resolve modal was inventing a candidate.
    // Offer an explicit create action in the dropdown so empty search doesn't look broken.
    if (allowCreate && trimmedInput.length >= 2 && !searchBusy && !hasExactMatch) {
      const alreadyOnetCreate = opts.some(
        (o) =>
          o.isOnet &&
          (o.label || "").toLowerCase() === trimmedInput.toLowerCase()
      );
      if (!alreadyOnetCreate) {
        opts.push({
          id: `__create__:${trimmedInput.toLowerCase()}`,
          label: `Add “${trimmedInput}”`,
          secondary:
            skillSearchResults.length === 0
              ? "Not in the catalog — look up & create"
              : "Create a new skill with this name",
          isCreate: true,
        });
      }
    }
    return opts;
  }, [
    skillSearchResults,
    allowCreate,
    includeOnet,
    trimmedInput,
    searchBusy,
    hasExactMatch,
  ]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  function setQuery(term) {
    const next = term || "";
    setInputValue(next);
    if (typeof props.onQueryChange === "function") {
      props.onQueryChange(next);
    }
  }

  function emitSelection(skill) {
    if (props.onSelect) {
      props.onSelect(skill);
      return;
    }
    if (props.onChange) {
      props.onChange(skill, props.field || "ID", "SkillSearch");
    }
  }

  async function beginCreateFlow(name) {
    const query = (name || "").trim();
    if (query.length < 2) return;

    setResolveState({
      query,
      candidates: [],
      allowBlankCreate: true,
      busy: true,
      error: "",
    });

    try {
      const resp = await API.postAsync("/Skill/Resolve", { Name: query });
      if (resp?.ok && resp.jsonData) {
        const data = resp.jsonData;
        setResolveState({
          query: data.Query ?? data.query ?? query,
          candidates: data.Candidates ?? data.candidates ?? [],
          allowBlankCreate: data.AllowBlankCreate ?? data.allowBlankCreate ?? true,
          busy: false,
          error: "",
        });
      } else {
        setResolveState({
          query,
          candidates: [],
          allowBlankCreate: true,
          busy: false,
          error: resp?.jsonData?.message || "Could not look up this skill. You can still create it.",
        });
      }
    } catch {
      setResolveState({
        query,
        candidates: [],
        allowBlankCreate: true,
        busy: false,
        error: "Could not look up this skill. You can still create it.",
      });
    }
  }

  function finishPick(skill) {
    const selected = normalizeSkill(skill) || skill;
    const name = selected.Name || selected.name || "";
    // Bound fields (parent skill) keep the chosen name visible; free search clears for the next pick.
    if (displayField) {
      setQuery(name);
    } else {
      setQuery("");
    }
    updateSearchResults([]);
    emitSelection(selected);
  }

  function onSelect(name, option) {
    if (option?.isOnet) {
      if (!allowCreate) return;
      beginCreateFlow(option?.value?.Name || option?.label || trimmedInput || name);
      return;
    }
    if (option?.isCreate) {
      if (!allowCreate) return;
      beginCreateFlow(trimmedInput || name);
      return;
    }

    // Prefer the option payload — more reliable than re-finding by label after async search.
    const fromOption =
      option?.value && typeof option.value === "object" ? option.value : null;
    if (fromOption && (fromOption.ID || fromOption.id) > 0 && !fromOption.isOnet) {
      finishPick(fromOption);
      return;
    }

    if (!name || typeof name !== "string") return;
    let value = name.trim();
    if (value.startsWith("Add Skill:")) value = value.replace(/^Add Skill:\s*/i, "").trim();
    if (value.startsWith("Add “") || value.startsWith('Add "')) {
      if (allowCreate) beginCreateFlow(trimmedInput);
      return;
    }

    const pool = searchData.results?.length ? searchData.results : skillSearchResults;
    const found =
      pool.find(
        (r) =>
          ((r.Name || "").toLowerCase() === value.toLowerCase() ||
            (r.ShortName || "").toLowerCase() === value.toLowerCase()) &&
          (r.ID || 0) > 0 &&
          !r.isOnet
      ) || null;

    if (props.onSelect || props.onChange) {
      if (found) {
        finishPick(found);
        return;
      }
      if (allowCreate && value.length >= 2) {
        beginCreateFlow(value);
        return;
      }
      // Bound parent field: ignore free-text that isn't a catalog skill.
      if (displayField) return;
      finishPick({ ID: 0, Name: value, ShortName: value });
      return;
    }

    if (!found || !found.ID) return;

    if (props.personEdit) {
      const known = knownSkills.some((s) => (s.ID || s.id) === found.ID);
      if (!known) {
        setPendingSkill(found);
        setDlgOpen(true);
      } else {
        navigate("/personSkill/" + found.ID);
      }
    } else {
      navigate("/adminSkillEdit/" + found.ID);
    }
  }

  async function onDlgResponse(confirmed) {
    setDlgOpen(false);
    setQuery("");
    if (!confirmed || !pendingSkill?.ID) return;
    navigate("/personSkill/" + pendingSkill.ID);
  }

  function onSkillSearchAsync(_e, newValue) {
    const term = newValue || "";
    setQuery(term);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!term || term.trim().length < 2) {
      updateSearchResults([]);
      setSearchBusy(false);
      return;
    }

    const trimmed = term.trim();

    // Instant client filter while typing within a previous server result set.
    if (
      searchData.results?.length &&
      searchData.term &&
      trimmed.toLowerCase().startsWith(searchData.term.toLowerCase())
    ) {
      const needle = trimmed.toLowerCase();
      const filtered = searchData.results.filter(
        (v) =>
          (v.Name || "").toLowerCase().includes(needle) ||
          (v.ShortName || "").toLowerCase().includes(needle)
      );
      updateSearchResults(filtered);
    }

    setSearchBusy(true);
    const seq = ++searchSeq.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const resp = await Helpers.SkillsAsync(trimmed);
        if (seq !== searchSeq.current) return;
        const data = (Array.isArray(resp) ? resp : [])
          .map(normalizeSkill)
          .filter((s) => s && ((s.ID > 0 && !s.isOnet) || (includeOnet && s.isOnet)));

        // Catalog first, then O*NET suggestions.
        data.sort((a, b) => {
          if (!!a.isOnet !== !!b.isOnet) return a.isOnet ? 1 : -1;
          return (a.Name || "") < (b.Name || "") ? -1 : 1;
        });
        setSearchData({ term: trimmed, results: data });
        updateSearchResults(data);
      } finally {
        if (seq === searchSeq.current) setSearchBusy(false);
      }
    }, 200);
  }

  function onSkillCreated(saved) {
    setCreateDraft(null);
    if (!saved) return;
    const skill = normalizeSkill(saved) || {
      ID: saved.ID ?? saved.id ?? 0,
      Name: saved.Name ?? saved.name ?? "",
      ShortName: saved.ShortName ?? saved.shortName ?? saved.Name ?? saved.name ?? "",
    };
    setQuery("");
    updateSearchResults([]);
    if (skill.ID > 0) emitSelection(skill);
  }

  function onResolveSelect(candidate) {
    const matchedId = Number(candidate?.MatchedSkillID) || 0;
    if (matchedId > 0) {
      setResolveState(null);
      setQuery("");
      updateSearchResults([]);
      emitSelection({
        ID: matchedId,
        Name: candidate.Name,
        ShortName: candidate.ShortName || candidate.Name,
      });
      return;
    }

    const query = resolveState?.query || candidate?.Name || "";
    setResolveState(null);
    setCreateDraft(candidateToDraft(candidate, query));
  }

  function onResolveCreateBlank() {
    const query = resolveState?.query || "";
    setResolveState(null);
    setCreateDraft(candidateToDraft({ Name: query, ShortName: query }, query));
  }

  function onResolveClose() {
    setResolveState(null);
  }

  return (
    <>
      <div className={`skill-search-row${showCreateButton ? " skill-search-row--with-add" : ""}`}>
        <div className="skill-search-row__field">
          <AutoComplete
            className={"skillSearch " + (props.className || "")}
            label={props.label || props.name || "Skill search"}
            value={inputValue}
            options={dropdownOptions}
            filterLocally={false}
            emptyMessage={
              trimmedInput.length >= 2 && !searchBusy && !allowCreate
                ? "No matching skills in the catalog"
                : searchBusy
                  ? includeOnet
                    ? "Searching catalog & O*NET…"
                    : "Searching catalog…"
                  : ""
            }
            onInputChange={onSkillSearchAsync}
            onSelect={onSelect}
          />
        </div>
        {showCreateButton ? (
          <button
            type="button"
            className="skill-search-add"
            title={`Add skill “${trimmedInput}”`}
            aria-label={`Add skill ${trimmedInput}`}
            data-testid="skill-search-add"
            onClick={() => beginCreateFlow(trimmedInput)}
          >
            <MdAdd size={22} aria-hidden />
          </button>
        ) : null}
      </div>
      <ConfirmDlg
        title="Add Skill?"
        content="Would you like to add this skill to your account?"
        open={dlgOpen}
        onResponse={onDlgResponse}
      />
      {resolveState ? (
        <SkillResolveModal
          query={resolveState.query}
          candidates={resolveState.candidates}
          allowBlankCreate={resolveState.allowBlankCreate}
          busy={resolveState.busy}
          error={resolveState.error}
          onSelect={onResolveSelect}
          onCreateBlank={onResolveCreateBlank}
          onClose={onResolveClose}
        />
      ) : null}
      {createDraft != null ? (
        <SkillEditModal
          skillId={0}
          initialDraft={createDraft}
          onClose={onSkillCreated}
        />
      ) : null}
    </>
  );
}
